import { useState, useEffect, useCallback } from 'react';
import { User } from '../../domain/entities/User';
import { AuthenticationPort } from '../../domain/ports/AuthenticationPort';

interface AuthenticationState {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface UseAuthenticationReturn extends AuthenticationState {
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateCGUAcceptanceUser: (user: User) => Promise<void>;
}

export const useAuthentication = (authenticationPort: AuthenticationPort): UseAuthenticationReturn => {
  const [state, setState] = useState<AuthenticationState>({
    user: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const signIn = useCallback(async (email: string, password: string) => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      const user = await authenticationPort.signIn(email, password);
      setState({
        user,
        isLoading: false,
        isAuthenticated: true,
      });
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [authenticationPort]);

  const signOut = useCallback(async () => {
    setState(prev => ({ ...prev, isLoading: true }));
    try {
      await authenticationPort.signOut();
      setState({
        user: null,
        isLoading: false,
        isAuthenticated: false,
      });
    } catch (error) {
      setState(prev => ({ ...prev, isLoading: false }));
      throw error;
    }
  }, [authenticationPort]);

  const updateCGUAcceptanceUser = useCallback(async (user: User) => {
    setState(prev => ({
      ...prev,
      user,
      isAuthenticated: !!user,
    }));
  }, []);

  useEffect(() => {
    const unsubscribe = authenticationPort.onAuthStateChanged((user) => {
      setState({
        user,
        isLoading: false,
        isAuthenticated: !!user,
      });
    });

    return unsubscribe;
  }, [authenticationPort]);

  return {
    ...state,
    signIn,
    signOut,
    updateCGUAcceptanceUser,
  };
};